import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { openVoiceCall, setLanguage } from "../stores/aiChatSlice";
import { useVoice } from "../hooks/useVoice";
import { VoicePhase } from "../hooks/voiceContext";

// ---------------------------------------------------------------------------
// Voice UI pieces, all inline-styled like the rest of the panel:
//   • VoiceHeaderToggles — "Voice mode" (hands-free) + "Read aloud" buttons
//   • MicButton          — push-to-talk button next to the composer input
//   • VoiceStatusStrip   — listening / transcribing / thinking / speaking bar
// ---------------------------------------------------------------------------

const GREEN = "var(--color-success, #156548)";
const BORDER = "1px solid var(--color-black-3, #e5e7eb)";

const PHASE_LABEL: Record<Exclude<VoicePhase, "idle">, string> = {
  listening: "Listening… speak now",
  transcribing: "Got it, writing that down…",
  thinking: "Thinking…",
  speaking: "Speaking…",
};

function pillStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? GREEN : "transparent",
    color: active ? "#fff" : "var(--color-text-secondary, #555)",
    border: active ? `1px solid ${GREEN}` : BORDER,
    borderRadius: 6,
    padding: "4px 8px",
    cursor: "pointer",
    fontSize: 11,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  };
}

/** Reply-language pill (Malayalam ⇄ English) — lives on the header's title row. */
export function LanguageToggle() {
  const dispatch = useAppDispatch();
  const language = useAppSelector((s) => s.aiChat.language);
  return (
    <button
      onClick={() => dispatch(setLanguage(language === "ml" ? "en" : "ml"))}
      title={language === "ml" ? "Replies in Malayalam — click for English" : "Replies in English — click for Malayalam"}
      style={pillStyle(false)}
    >
      {language === "ml" ? "മലയാളം" : "English"}
    </button>
  );
}

/** Voice actions (live call, telecaller, read aloud) — the header's toolbar row. */
export function VoiceHeaderToggles({ withLanguage = true }: { withLanguage?: boolean } = {}) {
  const dispatch = useAppDispatch();
  const hasModel = useAppSelector((s) => s.aiChat.currentModelId !== null);
  const { readAloud, supported, toggleReadAloud } = useVoice();
  return (
    <>
      {withLanguage && <LanguageToggle />}
      {supported && (
        <button
          onClick={() => dispatch(openVoiceCall())}
          disabled={!hasModel}
          title="Start a live voice call with Xale AI"
          style={{ ...pillStyle(true), opacity: hasModel ? 1 : 0.5, cursor: hasModel ? "pointer" : "not-allowed" }}
        >
          <MicGlyph size={12} />
          Voice
        </button>
      )}
      {supported && (
        <button
          onClick={() => dispatch(openVoiceCall({ mode: "telecaller" }))}
          disabled={!hasModel}
          title="Xale AI phones a lead as your telecaller — pick the lead, then it speaks first (Malayalam by default)"
          style={{ ...pillStyle(false), opacity: hasModel ? 1 : 0.5, cursor: hasModel ? "pointer" : "not-allowed" }}
        >
          <PhoneGlyph size={12} />
          Telecaller
        </button>
      )}
      {supported && (
        <button
          onClick={toggleReadAloud}
          title={readAloud ? "Read aloud is on — typed answers are spoken too" : "Read answers aloud"}
          aria-pressed={readAloud}
          style={pillStyle(readAloud)}
        >
          <SpeakerGlyph size={12} muted={!readAloud} />
        </button>
      )}
    </>
  );
}

export function MicButton({ disabled }: { disabled?: boolean }) {
  const dispatch = useAppDispatch();
  const { phase, supported } = useVoice();
  if (!supported) return null;
  const listening = phase === "listening";
  const busy = phase === "transcribing" || phase === "thinking";
  const off = disabled || busy;

  return (
    <button
      onClick={() => dispatch(openVoiceCall())}
      disabled={off}
      title="Talk to Xale AI — opens the live voice call"
      aria-label="Start voice call"
      style={{
        width: 36,
        height: 36,
        flex: "0 0 auto",
        borderRadius: 8,
        border: listening ? `1px solid ${GREEN}` : BORDER,
        background: listening ? GREEN : "#fff",
        color: listening ? "#fff" : off ? "#9ca3af" : "var(--color-text-secondary, #555)",
        cursor: off ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: listening ? "0 0 0 4px rgba(21,101,72,0.18)" : "none",
        transition: "box-shadow 160ms, background 160ms",
      }}
    >
      {listening ? <StopGlyph size={14} /> : <MicGlyph size={16} />}
    </button>
  );
}

export function VoiceStatusStrip() {
  const { phase, level, error, handsFree, stopAll } = useVoice();
  if (phase === "idle" && !error) return null;

  const bars = 5;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 8,
        padding: "6px 8px",
        borderRadius: 8,
        background: error && phase === "idle" ? "#fff7ed" : "#f0fdf4",
        border: `1px solid ${error && phase === "idle" ? "#fed7aa" : "#bbf7d0"}`,
        fontSize: 12,
        color: error && phase === "idle" ? "#9a3412" : "#14532d",
      }}
    >
      {phase === "listening" ? (
        <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2, height: 14 }}>
          {Array.from({ length: bars }).map((_, i) => {
            const lit = level * bars >= i + 0.5;
            return (
              <span
                key={i}
                style={{
                  width: 3,
                  height: 4 + i * 2.5,
                  borderRadius: 2,
                  background: lit ? GREEN : "#bbf7d0",
                  transition: "background 80ms",
                }}
              />
            );
          })}
        </span>
      ) : phase !== "idle" ? (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: GREEN,
            animation: "xaleVoicePulse 1s ease-in-out infinite",
          }}
        />
      ) : null}
      <span style={{ flex: 1 }}>
        {phase === "idle" ? error : PHASE_LABEL[phase]}
        {phase !== "idle" && handsFree ? " · voice mode" : ""}
      </span>
      {phase !== "idle" && (
        <button
          onClick={stopAll}
          style={{
            background: "transparent",
            border: "1px solid #bbf7d0",
            borderRadius: 6,
            padding: "2px 8px",
            fontSize: 11,
            cursor: "pointer",
            color: "#14532d",
          }}
        >
          Stop
        </button>
      )}
      <style>{`@keyframes xaleVoicePulse{0%,100%{opacity:.35;transform:scale(.8)}50%{opacity:1;transform:scale(1.15)}}`}</style>
    </div>
  );
}

// ---- tiny inline glyphs (no icon lib dependency inside the panel) ---------

function MicGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v4M8 21h8" />
    </svg>
  );
}

function PhoneGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.7 15.4l-3.3-1.4a1.6 1.6 0 0 0-1.8.4l-1.4 1.5a13 13 0 0 1-5.1-5.1l1.5-1.4a1.6 1.6 0 0 0 .4-1.8L10.6 4.3A1.6 1.6 0 0 0 8.8 3.4L5.4 4.3A1.6 1.6 0 0 0 4.2 5.9C4.6 13.4 10.6 19.4 18.1 19.8a1.6 1.6 0 0 0 1.6-1.2l.9-3.4a1.6 1.6 0 0 0-.9-1.8z" />
    </svg>
  );
}

function StopGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  );
}

function SpeakerGlyph({ size, muted }: { size: number; muted: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 5 6 9H2v6h4l5 4z" />
      {muted ? (
        <path d="M23 9l-6 6M17 9l6 6" />
      ) : (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 5a9 9 0 0 1 0 14" />
        </>
      )}
    </svg>
  );
}
